import { useCallback, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Brand, Vcard, VcardInput } from '../types'
import { Icons } from './icons'
import { Field } from './common/Field'
import { SectionCard } from './common/SectionCard'
import { ImageDropzone } from './common/ImageDropzone'
import { VcardPreview } from './preview/VcardPreview'
import { useVcardForm, type VcardFormField } from '../hooks/useVcardForm'
import { brandColor, brandInitial } from '../utils/brand'
import { slugify } from '../../utils/slug'

/** Tag-Labels für den Wochentag-Selector der Öffnungszeiten (1=Mo … 7=So). */
const DAY_LABELS: Record<number, string> = {
  1: 'Mo',
  2: 'Di',
  3: 'Mi',
  4: 'Do',
  5: 'Fr',
  6: 'Sa',
  7: 'So',
}
const DAY_KEYS = [1, 2, 3, 4, 5, 6, 7]

export interface VcardEditorViewProps {
  vcard: Vcard
  brand: Brand
  /** Hinweistext in der Action-Bar (Rollen-Scope). */
  scopeHint: string
  /** Zurück-Button anzeigen (nicht für die eigene vCard des Users). */
  showBack: boolean
  /** Ziel-Route des Zurück-/Verwerfen-Buttons (bei showBack). */
  backTo?: string
  saving: boolean
  onSave: (payload: VcardInput, cleared: VcardFormField[]) => void
}

/**
 * Ein Feld im 12-Spalten-Raster. Inline-`gridColumn` (kein dynamischer
 * Tailwind-Klassenname → wird nicht wegge-purged). Bei `grid-cols-1` (mobil)
 * klemmt der Span auf die einzelne Spalte = volle Breite.
 */
function Col({ span, children }: { span: number; children: ReactNode }) {
  return <div style={{ gridColumn: `span ${span} / span ${span}` }}>{children}</div>
}

/**
 * Präsentativer Editor-Körper einer vCard: gruppierte Sektionen im
 * 12-Spalten-Raster (links) plus sticky Live-Vorschau (rechts) und fixe
 * Action-Bar mit Tastatur-Steuerung (⌘/Strg+S, Esc) — BMC-Designsystem.
 * Wird von VcardEditorPage und MyVcardPage geteilt; Laden/Speichern besitzt die Page.
 */
export function VcardEditorView({
  vcard,
  brand,
  scopeHint,
  showBack,
  backTo,
  saving,
  onSave,
}: VcardEditorViewProps) {
  const navigate = useNavigate()
  const form = useVcardForm(vcard)
  const { values, setField, setActive, reset, preview } = form

  const displayName =
    values.firstName || values.lastName ? `${values.firstName} ${values.lastName}`.trim() : 'Neue vCard'

  // Öffentlicher Slug: bei Bestands-vCards fix, bei neuer aus dem Namen abgeleitet.
  const slugPreview = vcard.urlKey || slugify(`${values.firstName} ${values.lastName}`)

  const bind = (field: VcardFormField): { value: string; onChange: (v: string) => void } => ({
    value: values[field],
    onChange: (v) => setField(field, v),
  })

  const handleDiscard = useCallback((): void => {
    if (showBack && backTo) navigate(backTo)
    else reset()
  }, [showBack, backTo, navigate, reset])

  const handleSave = useCallback((): void => {
    if (!saving) onSave(form.buildPayload(), form.clearedFields())
  }, [saving, onSave, form])

  // Tastatur: ⌘/Strg+S = Speichern, Esc = Verwerfen/Zurück.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        handleSave()
      } else if (e.key === 'Escape') {
        handleDiscard()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleSave, handleDiscard])

  return (
    <>
      {/* Kopf: Marke, Name, Status, öffentlicher Slug */}
      <div className="mb-5 flex flex-wrap items-center gap-3.5">
        <span
          className="grid size-11 flex-none place-items-center rounded-xl text-lg font-extrabold text-white"
          style={{ background: brandColor(brand) }}
        >
          {brandInitial(brand)}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 font-display text-2xl font-semibold leading-tight text-foreground">
            {displayName}
            <span className={`badge ${values.active ? 'active' : 'inactive'}`}>
              {values.active && <span className="dot" />}
              {values.active ? 'aktiv' : 'inaktiv'}
            </span>
          </h1>
          <div className="truncate text-sm text-muted-foreground">
            {brand.brandName} · öffentlich unter{' '}
            <span className="font-mono text-foreground/80">/{slugPreview || '…'}</span>
          </div>
        </div>
        {showBack && backTo && (
          <button type="button" className="btn ghost sm" onClick={() => navigate(backTo)}>
            {Icons.back} Zurück
          </button>
        )}
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
        {/* ================= Formular ================= */}
        <div className="min-w-0 pb-4">
          <SectionCard icon={Icons.id} title="Profil">
            <ImageDropzone
              value={values.profileImageUrl}
              onChange={(v) => setField('profileImageUrl', v)}
              label="Profilbild"
              hint="JPG/PNG · quadratisch empfohlen · wird als base64 gespeichert"
              round
            />
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-12">
              <Col span={6}>
                <Field id="firstName" label="Vorname" {...bind('firstName')} />
              </Col>
              <Col span={6}>
                <Field id="lastName" label="Nachname" {...bind('lastName')} />
              </Col>
              <Col span={12}>
                <Field id="role" label="Position / Rolle" icon={Icons.user1} {...bind('role')} />
              </Col>
            </div>
          </SectionCard>

          <SectionCard icon={Icons.mail} title="Kontakt" hint="leer lassen entfernt das Feld">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              <Col span={6}>
                <Field id="email" label="E-Mail" type="email" optional icon={Icons.mail} {...bind('email')} />
              </Col>
              <Col span={6}>
                <Field id="phone" label="Telefon" optional icon={Icons.phone} {...bind('phone')} />
              </Col>
              <Col span={6}>
                <Field id="mobile" label="Mobil" optional icon={Icons.mobile} {...bind('mobile')} />
              </Col>
              <Col span={6}>
                <Field id="whatsapp" label="WhatsApp" optional icon={Icons.chat} {...bind('whatsapp')} />
              </Col>
            </div>
          </SectionCard>

          <SectionCard icon={Icons.web} title="Social & Links">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              <Col span={6}>
                <Field id="linkedin" label="LinkedIn" type="url" optional icon={Icons.in} {...bind('linkedin')} />
              </Col>
              <Col span={6}>
                <Field id="facebook" label="Facebook" type="url" optional icon={Icons.fb} {...bind('facebook')} />
              </Col>
              <Col span={6}>
                <Field id="website" label="Website" type="url" optional icon={Icons.web} {...bind('website')} />
              </Col>
              <Col span={6}>
                <Field
                  id="googleBusinessMessage"
                  label="Google Business"
                  type="url"
                  optional
                  icon={Icons.google}
                  {...bind('googleBusinessMessage')}
                />
              </Col>
            </div>
          </SectionCard>

          <SectionCard icon={Icons.pin} title="Standort & Team">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              <Col span={12}>
                <Field id="teamName" label="Team-Name" optional icon={Icons.users} {...bind('teamName')} />
              </Col>
              <Col span={12}>
                <ImageDropzone
                  value={values.teamLogo}
                  onChange={(v) => setField('teamLogo', v)}
                  label="Team-Logo"
                  hint="PNG, JPG oder SVG · SVG bleibt gestochen scharf · wird als base64 gespeichert"
                />
              </Col>
              <Col span={9}>
                <Field id="street" label="Straße" icon={Icons.pin} {...bind('street')} />
              </Col>
              <Col span={3}>
                <Field id="houseNumber" label="Hausnummer" {...bind('houseNumber')} />
              </Col>
              <Col span={4}>
                <Field id="zip" label="PLZ" {...bind('zip')} />
              </Col>
              <Col span={8}>
                <Field id="city" label="Ort" {...bind('city')} />
              </Col>
            </div>
          </SectionCard>

          <SectionCard icon={Icons.clock} title="Öffnungszeiten" hint="mehrere Tage pro Zeile möglich">
            <div className="mt-1 flex flex-col gap-3">
              {values.hours.map((hour, index) => (
                <div key={index} className="rounded-[10px] border border-border p-3">
                  <div className="flex flex-wrap gap-1.5">
                    {DAY_KEYS.map((day) => {
                      const on = hour.days.includes(day)
                      return (
                        <button
                          key={day}
                          type="button"
                          aria-pressed={on}
                          className="h-8 min-w-9 rounded-lg px-2.5 text-xs font-semibold transition-colors"
                          style={{
                            background: on ? 'var(--tint-blue)' : 'var(--bmc-bg)',
                            color: on ? 'var(--blue-2)' : 'var(--bmc-fg-3)',
                            border: `1px solid ${on ? 'transparent' : 'var(--bmc-line-2)'}`,
                          }}
                          onClick={() => form.toggleDay(index, day)}
                        >
                          {DAY_LABELS[day]}
                        </button>
                      )
                    })}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <label className="sr-only" htmlFor={`open_${index}`}>
                      Öffnet
                    </label>
                    <input
                      id={`open_${index}`}
                      type="time"
                      className="inp"
                      style={{ width: '132px' }}
                      value={hour.open}
                      onChange={(e) => form.setHour(index, { open: e.target.value })}
                    />
                    <span className="text-muted-foreground">–</span>
                    <label className="sr-only" htmlFor={`close_${index}`}>
                      Schließt
                    </label>
                    <input
                      id={`close_${index}`}
                      type="time"
                      className="inp"
                      style={{ width: '132px' }}
                      value={hour.close}
                      onChange={(e) => form.setHour(index, { close: e.target.value })}
                    />
                    <div className="flex-1" />
                    <button type="button" className="btn ghost sm" onClick={() => form.removeHour(index)}>
                      Entfernen
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="flex h-11 items-center justify-center gap-2 rounded-[10px] border border-dashed border-border text-sm font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                onClick={() => form.addHour()}
              >
                {Icons.plus} Tag hinzufügen
              </button>
            </div>
          </SectionCard>

          <SectionCard icon={Icons.toggle} title="Sichtbarkeit">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-foreground">Öffentlich sichtbar</div>
                <div className="text-xs text-muted-foreground">Inaktive vCards sind unter ihrer URL nicht abrufbar</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={values.active}
                aria-label="Öffentlich sichtbar"
                className={`switch${values.active ? ' on' : ''}`}
                onClick={() => setActive(!values.active)}
              />
            </div>
          </SectionCard>
        </div>

        {/* ================= Live-Vorschau (sticky) ================= */}
        <div className="lg:sticky lg:top-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="eyebrow">Live-Vorschau</span>
            <a
              className="btn ghost sm"
              href={vcard.publicUrl ?? `/${vcard.urlKey || slugPreview}`}
              target="_blank"
              rel="noreferrer"
            >
              {Icons.eye} Öffnen
            </a>
          </div>
          <VcardPreview data={preview} brand={brand} />
        </div>
      </div>

      {/* ================= Sticky Action-Bar (Tastatur-Hinweise) ================= */}
      <div className="sticky bottom-4 z-40 mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card/85 px-4 py-3 shadow-sm backdrop-blur">
        <span className="hidden items-center gap-3.5 text-xs text-muted-foreground sm:flex">
          <span className="flex items-center gap-1">
            <Kbd>⌘</Kbd>
            <Kbd>S</Kbd>
            <span className="ml-1">Speichern</span>
          </span>
          <span className="flex items-center gap-1">
            <Kbd>Esc</Kbd>
            <span className="ml-1">Verwerfen</span>
          </span>
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{scopeHint}</span>
        <button type="button" className="btn outline" onClick={handleDiscard}>
          Verwerfen
        </button>
        <button type="button" className="btn primary" disabled={saving} onClick={handleSave}>
          Speichern
        </button>
      </div>
    </>
  )
}

/** Tasten-Kappe für Tastatur-Hinweise. */
function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-md border border-b-2 border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
      {children}
    </kbd>
  )
}
